import { Group, Paper, Select, Skeleton, Text } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { TeamDailyVerlaufTag, Vorgangstyp } from "@msc/shared";

import { useTeamDailyVerlauf, useTeamDailyVerlaufTickets } from "../../hooks/useTeamDaily.js";
import { formatBerlinDateTime } from "../../lib/date.js";
import { CHART_COLORS } from "../../theme.js";
import { TicketListeModal } from "../tickets/TicketListeModal.js";

/** `YYYY-MM-DD` → `TT.MM.JJJJ` ohne Umweg über Date (keine Zeitzonenfalle). */
const tagDeutsch = (tag: string) => `${tag.slice(8, 10)}.${tag.slice(5, 7)}.${tag.slice(0, 4)}`;

/** Der Tag hinter einem Klick ins Diagramm (recharts `activePayload`), sonst null. */
export function tagAusDiagrammKlick(e: unknown): string | null {
  const p = (e as { activePayload?: Array<{ payload?: { tag?: string } }> } | null)
    ?.activePayload?.[0]?.payload?.tag;
  return p ? p : null;
}

/** Auswahl für den Tastaturzugang: `TT.MM. – <Zählertext>` je Tag. */
export function tagOptionen(
  tage: TeamDailyVerlaufTag[],
  zaehlertext: (erstellt: number, geloest: number) => string,
) {
  return tage.map((x) => ({
    value: x.tag,
    label: `${x.tag.slice(8, 10)}.${x.tag.slice(5, 7)}. – ${zaehlertext(x.erstellt, x.geloest)}`,
  }));
}

/**
 * Created vs. Resolved der letzten 30 Tage, aus dem Ticket-Spiegel. Ein Klick
 * auf einen Tag zeigt die Tickets dieses Tages. Darüber der Stand des letzten
 * Spiegel-Laufs — das Diagramm ist nicht live wie die Zähler.
 *
 * Offen liegt auf einer eigenen Achse rechts: Der Bestand ist hundertmal größer
 * als ein Tag, auf einer gemeinsamen Achse schrumpfen die Balken zu Strichen
 * (eazyBI tat es genauso, Rückmeldung 2026-10-15).
 */
export function VerlaufDiagramm({
  segmentId,
  teamId,
  vorgangstyp,
  bereit,
}: {
  segmentId: string | null;
  teamId: string | null;
  vorgangstyp: Vorgangstyp | null;
  bereit: boolean;
}) {
  const { t } = useTranslation();
  const verlauf = useTeamDailyVerlauf(segmentId, teamId, vorgangstyp, bereit);
  const [tag, setTag] = useState<string | null>(null);
  const tickets = useTeamDailyVerlaufTickets(segmentId, teamId, vorgangstyp, tag);

  if (verlauf.isLoading) return <Skeleton height={220} />;
  if (!verlauf.data)
    return verlauf.isError ? (
      <Text size="sm" c="red">
        {t("pages.teamDaily.kennzahlen.verlaufFehler")}
      </Text>
    ) : null;

  const erstellt = tickets.data?.erstellt ?? [];
  const geloest = tickets.data?.geloest ?? [];

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" align="flex-end" wrap="wrap" mb={4}>
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          {t("pages.teamDaily.kennzahlen.verlaufTitel")}
        </Text>
        <Select
          size="xs"
          aria-label={t("pages.teamDaily.kennzahlen.tagAnsehen")}
          placeholder={t("pages.teamDaily.kennzahlen.tagAnsehen")}
          value={tag}
          onChange={setTag}
          allowDeselect={false}
          data={tagOptionen(verlauf.data.tage, (e, g) =>
            t("pages.teamDaily.kennzahlen.tagZaehler", { erstellt: e, geloest: g }),
          )}
          style={{ minWidth: 200 }}
        />
      </Group>
      <Text size="xs" c="dimmed" mb={4}>
        {verlauf.data.stand
          ? t("pages.teamDaily.kennzahlen.stand", {
              zeit: formatBerlinDateTime(verlauf.data.stand),
            })
          : t("pages.teamDaily.kennzahlen.standUnbekannt")}
      </Text>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart
          data={verlauf.data.tage}
          onClick={(e) => {
            const p = tagAusDiagrammKlick(e);
            if (p) setTag(p);
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="tag" tickFormatter={(d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`} />
          <YAxis yAxisId="anzahl" allowDecimals={false} />
          <YAxis
            yAxisId="offen"
            orientation="right"
            allowDecimals={false}
            label={{ value: t("pages.teamDaily.kennzahlen.offen"), angle: 90, position: "insideRight" }}
          />
          <Tooltip />
          <Legend />
          <Bar
            yAxisId="anzahl"
            dataKey="erstellt"
            name={t("pages.teamDaily.kennzahlen.erstellt")}
            fill={CHART_COLORS.ist}
            cursor="pointer"
          />
          <Bar
            yAxisId="anzahl"
            dataKey="geloest"
            name={t("pages.teamDaily.kennzahlen.geloest")}
            fill={CHART_COLORS.positiv}
            cursor="pointer"
          />
          <Line
            yAxisId="offen"
            dataKey="offen"
            name={t("pages.teamDaily.kennzahlen.offen")}
            stroke={CHART_COLORS.soll}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <TicketListeModal
        opened={tag !== null}
        onClose={() => setTag(null)}
        titel={t("pages.teamDaily.kennzahlen.tagTitel", { tag: tag ? tagDeutsch(tag) : "" })}
        tickets={[]}
        // Zwei Abschnitte passend zu den Balken; ein Vorgang, der am selben Tag
        // erstellt und gelöst wurde, steht bewusst in beiden.
        abschnitte={[
          { titel: `${t("pages.teamDaily.kennzahlen.erstellt")} (${erstellt.length})`, tickets: erstellt },
          { titel: `${t("pages.teamDaily.kennzahlen.geloest")} (${geloest.length})`, tickets: geloest },
        ]}
        ohneBearbeiter
        jiraBaseUrl={verlauf.data.jiraBaseUrl}
        laedt={tickets.isLoading}
        fehler={tickets.isError ? t("pages.teamDaily.kennzahlen.listeFehler") : null}
      />
    </Paper>
  );
}
